import { tryCatch } from "@/lib/try-catch";
import { Ok, Err } from "ts-results";
import { API_BASE_URL } from "@/lib/constants";
import { Response } from "./types";
import { z } from "zod";

export const GeneralConfigurationRequestSchema = z.object({
  primaryColor: z.string().optional(),
  goodColor: z.string().optional(),
  warningColor: z.string().optional(),
  badColor: z.string().optional(),
  warningBadColor: z.string().optional(),
  headingColor: z.string().optional(),
  warningGoodColor: z.string().optional(),
  paperSize: z.string().optional(),
  marginInches: z.number().optional(),
  timezone: z.string().optional(),
  logoFile: z.instanceof(File).optional(),
  coverImage: z.boolean().optional(),
  laboratory: z.string().optional(),
  accreditationNumber: z.string().optional(),
  approvalNumber: z.string().optional(),
  outline: z.boolean().optional(),
  sectionDividers: z.boolean().optional(),
});

export type GeneralConfigurationRequest = z.infer<
  typeof GeneralConfigurationRequestSchema
>;

export const NoiseDoseConfigurationRequestSchema = z.object({
  decibelRounding: z.number().optional(),
  doseRounding: z.number().optional(),
  uncertaintyRounding: z.number().optional(),
  criterionLevel: z.number().optional(),
  exchangeRate: z.number().optional(),
  thresholdLevel: z.number().optional(),
  actionLevel: z.number().optional(),
});

export type NoiseDoseConfigurationRequest = z.infer<
  typeof NoiseDoseConfigurationRequestSchema
>;

export const TemperatureConfigurationRequestSchema = z.object({
  bodyTemperatureC: z.number().optional(),
  bodyTemperatureShiftC: z.number().optional(),
  temperatureRounding: z.number().optional(),
  clHeavy100: z.number().optional(),
  clHeavy75: z.number().optional(),
  clHeavy50: z.number().optional(),
  clHeavy25: z.number().optional(),
  clLight100: z.number().optional(),
  clLight75: z.number().optional(),
  clLight50: z.number().optional(),
  clLight25: z.number().optional(),
  clModerate100: z.number().optional(),
  clModerate75: z.number().optional(),
  clModerate50: z.number().optional(),
  clModerate25: z.number().optional(),
});

export type TemperatureConfigurationRequest = z.infer<
  typeof TemperatureConfigurationRequestSchema
>;

export const LightingConfigurationRequestSchema = z.object({
  wallReflectionLimit: z.number().optional(),
  workPlaneReflectionLimit: z.number().optional(),
  lengthRounding: z.number().optional(),
  icRounding: z.number().optional(),
  luxRounding: z.number().optional(),
  reflectionRounding: z.number().optional(),
});

export type LightingConfigurationRequest = z.infer<
  typeof LightingConfigurationRequestSchema
>;

export async function updateGeneralConfiguration(
  configuration: GeneralConfigurationRequest
): Promise<Response<string>> {
  const formData = new FormData();
  if (configuration.primaryColor) {
    formData.append("primaryColor", configuration.primaryColor);
  }
  if (configuration.goodColor) {
    formData.append("goodColor", configuration.goodColor);
  }
  if (configuration.warningColor) {
    formData.append("warningColor", configuration.warningColor);
  }
  if (configuration.badColor) {
    formData.append("badColor", configuration.badColor);
  }
  if (configuration.warningBadColor) {
    formData.append("warningBadColor", configuration.warningBadColor);
  }
  if (configuration.headingColor) {
    formData.append("headingColor", configuration.headingColor);
  }
  if (configuration.warningGoodColor) {
    formData.append("warningGoodColor", configuration.warningGoodColor);
  }
  if (configuration.paperSize) {
    formData.append("paperSize", configuration.paperSize);
  }
  if (configuration.marginInches) {
    formData.append("marginInches", configuration.marginInches.toString());
  }
  if (configuration.timezone) {
    formData.append("timezone", configuration.timezone);
  }
  if (configuration.logoFile) {
    formData.append("logoFile", configuration.logoFile);
  }
  if (configuration.laboratory) {
    formData.append("laboratory", configuration.laboratory);
  }
  if (configuration.accreditationNumber) {
    formData.append("accreditationNumber", configuration.accreditationNumber);
  }
  if (configuration.approvalNumber) {
    formData.append("approvalNumber", configuration.approvalNumber);
  }

  formData.append("coverImage", (!!configuration.coverImage).toString());
  formData.append("outline", (!!configuration.outline).toString());
  formData.append("sectionDividers", (!!configuration.sectionDividers).toString());
  const responseResult = await tryCatch(
    fetch(`${API_BASE_URL}/api/v1/configuracion/general`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
  );

  if (responseResult.err) {
    return Err({
      message: `Error al consultar el servidor: ${responseResult.val.message}`,
      devMessage: responseResult.val.message,
    });
  }

  const response = responseResult.val;

  if (!response.ok) {
    const errorMessageResult = await tryCatch(response.json());
    return errorMessageResult.err
      ? Err({
          message: `Error al actualizar la configuración: ${errorMessageResult.val.message}`,
          devMessage: errorMessageResult.val.message,
        })
      : Err({
          message: errorMessageResult.val.message,
          devMessage: errorMessageResult.val.devMessage,
        });
  }

  const dataResult = await tryCatch(response.text());

  if (dataResult.err) {
    return Err({
      message: `Error al leer la respuesta del servidor: ${dataResult.val.message}`,
      devMessage: dataResult.val.message,
    });
  }

  return Ok(dataResult.val);
}

export async function updateNoiseDoseConfiguration(
  configuration: NoiseDoseConfigurationRequest
): Promise<Response<string>> {
  const responseResult = await tryCatch(
    fetch(`${API_BASE_URL}/api/v1/configuracion/ruido-dosis`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(configuration),
    })
  );

  if (responseResult.err) {
    return Err({
      message: `Error al consultar el servidor: ${responseResult.val.message}`,
      devMessage: responseResult.val.message,
    });
  }

  const response = responseResult.val;

  if (!response.ok) {
    const errorMessageResult = await tryCatch(response.json());
    return errorMessageResult.err
      ? Err({
          message: `Error al actualizar la configuración: ${errorMessageResult.val.message}`,
          devMessage: errorMessageResult.val.message,
        })
      : Err({
          message: errorMessageResult.val.message,
          devMessage: errorMessageResult.val.devMessage,
        });
  }

  const dataResult = await tryCatch(response.text());

  if (dataResult.err) {
    return Err({
      message: `Error al leer la respuesta del servidor: ${dataResult.val.message}`,
      devMessage: dataResult.val.message,
    });
  }

  return Ok(dataResult.val);
}

export async function updateTemperatureConfiguration(
  configuration: TemperatureConfigurationRequest
): Promise<Response<string>> {
  const responseResult = await tryCatch(
    fetch(`${API_BASE_URL}/api/v1/configuracion/temperatura`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(configuration),
    })
  );

  if (responseResult.err) {
    return Err({
      message: `Error al consultar el servidor: ${responseResult.val.message}`,
      devMessage: responseResult.val.message,
    });
  }

  const response = responseResult.val;

  if (!response.ok) {
    const errorMessageResult = await tryCatch(response.json());
    return errorMessageResult.err
      ? Err({
          message: `Error al actualizar la configuración: ${errorMessageResult.val.message}`,
          devMessage: errorMessageResult.val.message,
        })
      : Err({
          message: errorMessageResult.val.message,
          devMessage: errorMessageResult.val.devMessage,
        });
  }

  const dataResult = await tryCatch(response.text());

  if (dataResult.err) {
    return Err({
      message: `Error al leer la respuesta del servidor: ${dataResult.val.message}`,
      devMessage: dataResult.val.message,
    });
  }

  return Ok(dataResult.val);
}

export async function updateLightingConfiguration(
  configuration: LightingConfigurationRequest
): Promise<Response<string>> {
  const responseResult = await tryCatch(
    fetch(`${API_BASE_URL}/api/v1/configuracion/iluminacion`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(configuration),
    })
  );

  if (responseResult.err) {
    return Err({
      message: `Error al consultar el servidor: ${responseResult.val.message}`,
      devMessage: responseResult.val.message,
    });
  }

  const response = responseResult.val;

  if (!response.ok) {
    const errorMessageResult = await tryCatch(response.json());
    return errorMessageResult.err
      ? Err({
          message: `Error al actualizar la configuración: ${errorMessageResult.val.message}`,
          devMessage: errorMessageResult.val.message,
        })
      : Err({
          message: errorMessageResult.val.message,
          devMessage: errorMessageResult.val.devMessage,
        });
  }

  const dataResult = await tryCatch(response.text());

  if (dataResult.err) {
    return Err({
      message: `Error al leer la respuesta del servidor: ${dataResult.val.message}`,
      devMessage: dataResult.val.message,
    });
  }

  return Ok(dataResult.val);
}
